// Out-of-specification (OOS) grading (z8nrz7eq4e, z8nrz7eq4h).
//
// Mirrors cgt-backend src/form-engine/evaluate-oos.ts. Keep the two in sync:
// the renderer grades a value here to show the flag and ask for a comment
// before saving, and the backend grades it again to enforce both.
//
// Number fields grade by range:
// - In spec is inclusive (`low <= value <= high`) and always wins.
// - Bands are half-open `[min, max)` and tried in order; first match grades.
// - An out-of-spec value no band contains is `high`, no comment required.
// - A blank or non-numeric value is never flagged.
//
// Text, textarea, dropdown, checkbox and date fields grade by rule: the first
// band (authored order) whose `when` matches grades the value. No match means
// in spec, and a blank value is never flagged. Matching is the conditional
// visibility comparison (`matchesCondition` in lib/formSections.ts).

import type { OosBand, OosConfig, OosOperator, OosSeverity, TableConfig } from '@/types/cgt'
import { matchesCondition } from '@/lib/formSections'

/** The field types a specification can be set on, in the saved vocabulary. */
export type OosFieldType = 'number' | 'text' | 'textarea' | 'dropdown' | 'checkbox' | 'date'

/**
 * The saved type for `fieldType`, or null when a specification is not
 * offered. Accepts the builder's names too (select, switch), so builder and
 * renderer can call the same helpers.
 */
export function oosFieldType(fieldType: string | undefined): OosFieldType | null {
  switch (fieldType) {
    case 'number':
    case 'text':
    case 'textarea':
    case 'dropdown':
    case 'checkbox':
    case 'date':
      return fieldType
    case 'select':
      return 'dropdown'
    case 'switch':
      return 'checkbox'
    default:
      return null
  }
}

/** True when a specification can be set on this field type. */
/**
 * How a field's value is graded: as its own type, or, for a calculated field,
 * as its result (mirrors oosGradingType in the backend's evaluate-oos.ts). A
 * calculated result shown as a number grades by range, one shown as a date by
 * date rules; a date-and-time result cannot carry a specification.
 */
export function oosGradingType(fieldType: string | undefined, displayFormat?: string): OosFieldType | null {
  if (fieldType === 'calculated') {
    if (displayFormat === 'date') return 'date'
    if (displayFormat === 'datetime') return null
    return 'number'
  }
  return oosFieldType(fieldType)
}

export function oosSupportedType(fieldType: string | undefined): boolean {
  return oosFieldType(fieldType) !== null
}

/** The comparisons each non-number type offers, in menu order. */
export const OOS_OPERATORS: Record<Exclude<OosFieldType, 'number'>, readonly OosOperator[]> = {
  text: ['equals', 'notEquals', 'contains', 'startsWith', 'endsWith'],
  textarea: ['equals', 'notEquals', 'contains', 'startsWith', 'endsWith'],
  dropdown: ['equals', 'notEquals'],
  checkbox: ['equals'],
  date: ['equals', 'notEquals', 'gt', 'gte', 'lt', 'lte'],
}

/** The comparisons `fieldType` offers. Empty for number and unsupported types. */
export function oosOperatorsFor(fieldType: string | undefined): readonly OosOperator[] {
  const t = oosFieldType(fieldType)
  return t && t !== 'number' ? OOS_OPERATORS[t] : []
}

/** How each comparison reads in the builder. Dates read the order words. */
export const OOS_OPERATOR_LABELS: Record<OosOperator, string> = {
  equals: 'is',
  notEquals: 'is not',
  contains: 'contains',
  startsWith: 'starts with',
  endsWith: 'ends with',
  gt: 'is after',
  gte: 'is on or after',
  lt: 'is before',
  lte: 'is on or before',
}

export const OOS_SEVERITIES: readonly OosSeverity[] = ['low', 'medium', 'high']

export const OOS_SEVERITY_LABELS: Record<OosSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
}

export const OOS_DEFAULT_COLORS: Record<OosSeverity, string> = {
  low: '#eab308',
  medium: '#f97316',
  high: '#dc2626',
}

export const OOS_COMMENT_REQUIRED_MESSAGE =
  'A comment is required for this out-of-specification value.'

export interface OosResult {
  severity: OosSeverity
  color: string
  commentRequired: boolean
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

export function oosNumericValue(value: unknown): number | null {
  if (isNum(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

const isBlank = (v: unknown) =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '')

const bandResult = (band: OosBand): OosResult => ({
  severity: band.severity,
  color: band.color || OOS_DEFAULT_COLORS[band.severity],
  commentRequired: band.commentRequired === true,
})

/** A band rule with a value to compare against. Rules without one never match. */
const hasRule = (b: OosBand): b is OosBand & { when: NonNullable<OosBand['when']> } =>
  !!b.when && typeof b.when.value === 'string' && b.when.value.trim() !== ''

/**
 * The first band whose rule matches `value`, for the non-number types, or
 * undefined. A blank value matches nothing.
 */
export function matchingOosRule(
  config: OosConfig,
  value: unknown,
): (OosBand & { when: NonNullable<OosBand['when']> }) | undefined {
  if (isBlank(value)) return undefined
  return (config.bands ?? []).filter(hasRule).find(b => matchesCondition(value, b.when.operator, b.when.value))
}

/**
 * Grades `value` against `config`. Null means no flag. `fieldType` defaults to
 * number, the only type there was before rules.
 */
export function evaluateOos(
  config: OosConfig | undefined | null,
  value: unknown,
  fieldType: string = 'number',
): OosResult | null {
  if (!config) return null
  const type = oosFieldType(fieldType)
  if (!type) return null
  if (type !== 'number') {
    const band = matchingOosRule(config, value)
    return band ? bandResult(band) : null
  }

  const n = oosNumericValue(value)
  if (n === null) return null
  const aboveLow = !isNum(config.low) || n >= config.low
  const belowHigh = !isNum(config.high) || n <= config.high
  if (aboveLow && belowHigh) return null

  const band = (config.bands ?? []).find(
    (b) => (!isNum(b.min) || n >= b.min) && (!isNum(b.max) || n < b.max),
  )
  if (!band) return { severity: 'high', color: OOS_DEFAULT_COLORS.high, commentRequired: false }
  return bandResult(band)
}

/** True when the two saved values are the same entry (equal numbers, or both blank). */
export function sameOosEntry(a: unknown, b: unknown): boolean {
  const na = oosNumericValue(a)
  const nb = oosNumericValue(b)
  if (na !== null || nb !== null) return na === nb
  return (a ?? '') === (b ?? '')
}

const isChecked = (v: unknown) => v === true || v === 'true'

/**
 * True when `a` and `b` are the same entry for a field of `fieldType`: equal
 * numbers, the same checked state, or the same trimmed text. Blank equals
 * blank. Matches a saved alert to the value on the form.
 */
export function sameOosValue(fieldType: string, a: unknown, b: unknown): boolean {
  const type = oosFieldType(fieldType)
  if (type === 'number') return sameOosEntry(a, b)
  if (isBlank(a) || isBlank(b)) return isBlank(a) && isBlank(b)
  if (type === 'checkbox') return isChecked(a) === isChecked(b)
  return String(a).trim() === String(b).trim()
}

/** The value as a person reads it: a checkbox is Checked or Unchecked. */
export function formatOosValue(fieldType: string, value: unknown): string {
  if (isBlank(value)) return ''
  if (oosFieldType(fieldType) === 'checkbox') return isChecked(value) ? 'Checked' : 'Unchecked'
  return String(value)
}

/** One rule as words: `is "Fail"`, `is Checked`, `is after 2026-01-31`. */
export function describeOosRule(fieldType: string, when: NonNullable<OosBand['when']>): string {
  const op = OOS_OPERATOR_LABELS[when.operator] ?? when.operator
  const type = oosFieldType(fieldType)
  if (type === 'checkbox') return `${op} ${formatOosValue('checkbox', when.value)}`
  if (type === 'date') return `${op} ${when.value}`
  return `${op} "${when.value}"`
}

/**
 * The reason the builder cannot save this config, in author-facing words, or
 * null when it is valid. Same checks as the backend's oosConfigProblem.
 * `options` are a dropdown's inline options; a rule must name one of them.
 */
export function oosConfigProblem(
  config: OosConfig,
  fieldType: string = 'number',
  options?: readonly string[],
): string | null {
  const type = oosFieldType(fieldType)
  if (!type) return 'This field type cannot have a specification.'
  if (type === 'number') {
    if (!isNum(config.low) && !isNum(config.high)) return 'Set a low limit, a high limit, or both.'
    if (isNum(config.low) && isNum(config.high) && config.low > config.high) {
      return 'The low limit must not be above the high limit.'
    }
    for (const [i, b] of (config.bands ?? []).entries()) {
      if (!isNum(b.min) && !isNum(b.max)) return `Band ${i + 1} needs a "from" value, a "to" value, or both.`
      if (isNum(b.min) && isNum(b.max) && b.min >= b.max) return `Band ${i + 1} must start below where it ends.`
    }
  } else {
    const bands = config.bands ?? []
    if (bands.length === 0) return 'Add at least one rule.'
    const allowed = OOS_OPERATORS[type]
    for (const [i, b] of bands.entries()) {
      if (!b.when || !allowed.includes(b.when.operator)) return `Rule ${i + 1} needs a comparison.`
      if (!hasRule(b)) return `Rule ${i + 1} needs a value.`
      if (type === 'checkbox' && b.when.value !== 'true' && b.when.value !== 'false') {
        return `Rule ${i + 1} must be Checked or Unchecked.`
      }
      if (type === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(b.when.value.trim())) {
        return `Rule ${i + 1} needs a date.`
      }
      if (type === 'dropdown' && options && options.length > 0 && !options.includes(b.when.value)) {
        return `Rule ${i + 1}: "${b.when.value}" is not one of the options.`
      }
    }
  }
  if ((config.alertGroupIds ?? []).length === 0) return 'Choose at least one group to alert.'
  return null
}

/**
 * The config as it is saved: only finite numbers, a valid severity, a colour
 * only when it differs from the default, and no duplicate groups. Number
 * fields keep ranges and drop rules; other supported types keep rules (with
 * a comparison the type offers and a value) and drop ranges. `emailGroups` is
 * kept only when on. Undefined when there is nothing to save, so a field
 * without a specification stays exactly as it was.
 */
export function sanitizeOosConfig(
  fieldType: string,
  config: OosConfig | undefined,
  displayFormat?: string,
): OosConfig | undefined {
  const type = oosGradingType(fieldType, displayFormat)
  // Nobody types a calculated value, so no save can be asked for a comment.
  const noComment = fieldType === 'calculated'
  if (!config || !type) return undefined
  const isNumber = type === 'number'
  const allowed = isNumber ? [] : OOS_OPERATORS[type]
  const bands: OosBand[] = (config.bands ?? [])
    .filter((b) => OOS_SEVERITIES.includes(b.severity))
    .filter((b) => isNumber || (hasRule(b) && allowed.includes(b.when.operator)))
    .map((b) => ({
      ...(isNumber && isNum(b.min) ? { min: b.min } : {}),
      ...(isNumber && isNum(b.max) ? { max: b.max } : {}),
      ...(!isNumber && b.when ? { when: { operator: b.when.operator, value: b.when.value } } : {}),
      severity: b.severity,
      ...(b.color && /^#[0-9a-fA-F]{6}$/.test(b.color) && b.color.toLowerCase() !== OOS_DEFAULT_COLORS[b.severity]
        ? { color: b.color }
        : {}),
      commentRequired: !noComment && b.commentRequired === true,
    }))
  return {
    ...(isNumber && isNum(config.low) ? { low: config.low } : {}),
    ...(isNumber && isNum(config.high) ? { high: config.high } : {}),
    bands,
    alertGroupIds: [...new Set(config.alertGroupIds ?? [])],
    ...(config.emailGroups === true ? { emailGroups: true } : {}),
  }
}

/**
 * A table's column and cell specifications as they are saved, each through
 * `sanitizeOosConfig` for the type it grades: the column's type for a column,
 * the cell's resolved type for a cell. The same config back when the table
 * has none, so a table without specifications saves exactly as before.
 */
export function sanitizeTableOos(config: TableConfig): TableConfig {
  const cells = config.cells ?? {}
  const hasAny = config.columns.some(c => c.oos) || Object.values(cells).some(c => c.oos)
  if (!hasAny) return config
  const typeOfColumn = new Map(config.columns.map(c => [c.id, c.columnType]))
  return {
    ...config,
    columns: config.columns.map(c => (c.oos ? { ...c, oos: sanitizeOosConfig(c.columnType, c.oos) } : c)),
    ...(config.cells
      ? {
          cells: Object.fromEntries(
            Object.entries(cells).map(([key, cell]) => {
              if (!cell.oos) return [key, cell]
              const colId = key.slice(key.indexOf('__') + 2)
              const type = cell.type ?? typeOfColumn.get(colId) ?? ''
              return [key, { ...cell, oos: sanitizeOosConfig(type, cell.oos) }]
            }),
          ),
        }
      : {}),
  }
}

/** "90 to 100", "at least 90", "at most 100" — how the in-spec range reads. */
export function describeOosRange(config: Pick<OosConfig, 'low' | 'high'>): string {
  if (isNum(config.low) && isNum(config.high)) return `${config.low} to ${config.high}`
  if (isNum(config.low)) return `at least ${config.low}`
  if (isNum(config.high)) return `at most ${config.high}`
  return ''
}
